import { useEffect, useState, useCallback } from "react";
import { useSession, LogoutReason } from "../Auth/hooks/useSession";
import AuthLogoutMessage from "../Auth/components/AuthLogoutMessage";
import { getFetchOptions } from "../../utils/fetch";
import useFetch from "../../hooks/useFetch";
import useFetchAuto from "../../hooks/useFetchAuto";
import LoadingSpinner from "../../components/loadingSpinner/LoadingSpinner";
import Modal from "../../components/modals/Modal";
import MessageModal, { MessageModalType } from "../../components/modals/MessageModal";
import Button, { ButtonType } from "../../components/Button";
import DataTable from "../../components/DataTable";
import { formatDate } from "../../utils/formatting";
import { faEnvelope, faCircleCheck, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import SelectPackages from "../../components/packages/SelectPackages";
import EditCustomerUser from "./EditCustomerUser";
import {
	CompanyUserRole,
	AssignableRoleOptionsBase,
	roleLabel
} from "../../utils/companyUserRole";
import "./ViewCustomer.scss";

const ViewCustomer = (props) => {
	const INITIAL_DATA = {
		businessName: { value: "", validation: 0 },
		businessAddress: { value: "", validation: 0 },
		userFirstName: { value: "", validation: 0 },
		userLastName: { value: "", validation: 0 },
		userEmail: { value: "", validation: 0 },
		currentCustomerPackages: []
	};
	const [packageData, setPackageData] = useState(INITIAL_DATA);
	const { reason } = useSession();
	const [deleting, setDeleting] = useState(false);
	const [resending, setResending] = useState(false);
	const [users, setUsers] = useState([]);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [confirmResend, setConfirmResend] = useState(false);
	const { loading: loadingCustomer, data } = useFetchAuto("/api/CustomerById/" + props.item.Id);
	const { loading: loadingPackages, data: availablePackages } = useFetchAuto("/api/ListAssignablePackages/");
	const { ready, execute, executingFetch } = useFetch();
	const [customerAdminHasLoggedIn, setCustomerAdminHasLoggedIn] = useState(false);
	const [fatalError, setFatalError] = useState(false);
	const { disableUserModification } = props;
	const [showErrorModal, setErrorModal] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [canSubmit, setCanSubmit] = useState(false);

	const [showEditModal, setShowEditModal] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);

	const [confirmUserDelete, setConfirmUserDelete] = useState(false);
	const [userPendingDelete, setUserPendingDelete] = useState(null);

	const [showAddUserModal, setShowAddUserModal] = useState(false);
	const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
	const [pendingSave, setPendingSave] = useState(null);

	// Perform REST request.
	useEffect(() => {
		if (ready && deleting) {
			const options = getFetchOptions({
				method: "DELETE"
			});
			execute("/api/Customer/" + props.item.Id, options, (error) => {
				setDeleting(false);
				props.getDataFromServer();
				if (error) {
					setErrorMessage(error.message);
					setErrorModal(true);
				}
				props.setModal(false);
			});
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ready, props.item, props.getDataFromServer, deleting, execute]); // props

	useEffect(() => {
		if (ready && data) {
			const options = getFetchOptions({
				method: "GET"
			});
			execute(`api/CustomerUsers/${data.CompanyId}`, options, (error, usersArray) => {
				if (error) {
					setErrorMessage(error.message);
					setErrorModal(true);
				}
				else {
					setUsers(usersArray || { users: [] });
					const hasCustomerAdminWithLogin = (usersArray ?? []).some(user =>
						user.RoleId === CompanyUserRole.CustomerAdmin &&
						user.LastLoginDate != null
					);
					
					setCustomerAdminHasLoggedIn(hasCustomerAdminWithLogin);
				}
			})
		}
	}, [data, ready, execute]);

	useEffect(() => {
		if (ready && resending) {
			const resendContactEmail = data.ContactEmail;
			const options = getFetchOptions({
				method: "POST",
				body: resendContactEmail
			});
			execute("/api/Provisioning/ResendRegistrationEmail", options, (error, data) => {
				setResending(false);
				if (error) {
					setFatalError(true);
				}
				props.setModal(false);
			});
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, props.item, ready, resending, execute]); // props

	function getUserFullName(user) {
		if (!user) {
			return "";
		}
		const fn = (user.FirstName ?? "").trim();
		const ln = (user.LastName ?? "").trim();
		return [fn, ln].filter(Boolean).join(" ");
	}

	function onChangeInput(fields) {
		setPackageData((prev) => {
			return { ...prev, ...fields }
		});
	}

	function onClickSubmit(enable) {
		setIsSubmitting(true);
		const pkgs = packageData.currentCustomerPackages.map(pkg => ({
			Id: pkg.Id ?? 0,
			PackageId: pkg.PackageId ?? pkg.ParentId ?? 0,
			Level: pkg.Level ?? 0,
			LevelId: pkg.LevelId ?? 0,
			Name: pkg.Name ?? pkg.PackageName ?? '',
			SeatBlocks: pkg.SeatQuantity ?? (typeof pkg.Seats === "string" ? parseInt(pkg.Seats) || 0 : pkg.Seats ?? 0),
			SeatBlockSize: pkg.SeatBlockSize ?? 0
		}));
		const options = getFetchOptions({
			method: "POST",
			body: {
				CustomerId: props.item.Id,
				Packages: pkgs
			}
		});
		execute("/api/CustomerPackage", options, (err) => {
			if (!err) {
				if (typeof props.onPackagesUpdate === "function") {
					props.onPackagesUpdate(packageData.currentCustomerPackages);
				}
				setIsSubmitting(false);
			}
			enable();
			setIsSubmitting(false);
			setCanSubmit(false);
		});
	}

	function onClickClose(modal) {
		props.setModal(modal);
	}

	function onClickDelete() {
		setConfirmDelete(true);
	}

	function onClickResend() {
		setConfirmResend(true);
	}

	function onClickConfirmResend(yes) {
		if (yes) {
			setResending(true);
		}
		setConfirmResend(false);
	}

	function onClickConfirmDelete(yes) {
		if (yes) {
			setDeleting(true);
		}
		setConfirmDelete(false);
	}

	const handleCloseModal = () => {
		setResending(false);
		window.location.reload();
	}

	const onCloseModal = () => {
		setErrorModal(false);
	};

	const handleDeleteUser = useCallback((user, enable) => {
		const body = {
			Id: Number(user.Id),
			FirstName: (user.FirstName ?? "").trim(),
			LastName: (user.LastName ?? "").trim(),
			Username: (user.Username ?? "").trim(),
			RoleId: Number(user.RoleId)
		};

		const options = getFetchOptions({ method: "DELETE", body });

		execute(`/api/customer-users`, options, (error, serverUser) => {
			if (error) {
				setErrorMessage(error.message || "Failed to update user.");
				setErrorModal(true);
				return;
			}

			setUsers((prev) => {
				const key = (u) => u?.Id ?? (u?.Username ?? u?.Email);
				const doomedKey = key(user);
				return prev.filter((u) => key(u) !== doomedKey);
			});

			setShowEditModal(false);
			setSelectedUser(null);
		});
	}, [execute]);

	const handleRequestUserDelete = useCallback((user) => {
		setUserPendingDelete(user);
		setConfirmUserDelete(true);
		setShowEditModal(false);
	}, []);

	const onClickConfirmDeleteUser = useCallback((yes) => {
		if (yes && userPendingDelete) {
			handleDeleteUser(userPendingDelete, null);
		}
		setConfirmUserDelete(false);
		setUserPendingDelete(null);
	}, [handleDeleteUser, userPendingDelete]);

	const handleUserAdded = useCallback((user, companyId) => {

		const body = {
			CompanyId: Number(companyId),
			Id: -1,
			FirstName: (user.FirstName ?? "").trim(),
			LastName: (user.LastName ?? "").trim(),
			Username: (user.Username ?? "").trim(),
			RoleId: Number(user.RoleId)
		};

		const options = getFetchOptions({ method: "POST", body });

		execute(`/api/customer-users`, options, (error, serverUser) => {
			if (error) {
				setErrorMessage(error.message || "Failed to add user.");
				setErrorModal(true);
				return;
			}

			setUsers((prev) => {
				const key = (u) => u?.Id ?? u?.Username;
				const newKey = key(serverUser ?? user);
				const filtered = prev.filter((u) => key(u) !== newKey);
				return [...filtered, serverUser ?? user];
			});

			setShowAddUserModal(false);
			setSelectedUser(null);
		});
	}, [execute]);


	const handleRequestSave = useCallback((userDraft, context) => {
		setPendingSave({ user: userDraft, isAdd: !!context?.isAdd, enable: context?.enable });
		setConfirmSaveOpen(true);
	}, []);

	const handleUserSaved = useCallback((updatedUser, companyId) => {
		if (!updatedUser) {
			return;
		}

		const body = {
			CompanyId: Number(companyId),
			Id: Number(updatedUser.Id),
			FirstName: (updatedUser.FirstName ?? "").trim(),
			LastName: (updatedUser.LastName ?? "").trim(),
			Username: (updatedUser.Username ?? "").trim(),
			RoleId: Number(updatedUser.RoleId)
		};

		const options = getFetchOptions({ method: "PUT", body });

		execute(`/api/customer-users`, options, (error, serverUser) => {
			if (error) {
				setErrorMessage(error.message || "Failed to update user.");
				setErrorModal(true);
				return;
			}

			setShowEditModal(false);
			setSelectedUser(null);
		});

		setUsers((prev) => {
			const key = (u) => u?.Id ?? u?.Username;
			const targetKey = key(updatedUser);
			return prev.map(u => (key(u) === targetKey ? { ...u, ...updatedUser } : u));
		});
	}, [execute]);

	const onClickConfirmSave = useCallback((yes) => {
		pendingSave?.enable?.();

		if (yes && pendingSave?.user) {
			if (pendingSave.isAdd) {
				handleUserAdded(pendingSave.user, data?.CompanyId);
				setShowAddUserModal(false);
			} else {
				handleUserSaved(pendingSave.user, data?.CompanyId);
				setShowEditModal(false);
			}
		}

		setConfirmSaveOpen(false);
		setPendingSave(null);
	}, [pendingSave, data?.CompanyId, handleUserAdded, handleUserSaved]);


	const handleDate = (dateString) => {
		if (!dateString) {
			return "Not logged in";
		}
		const date = new Date(dateString);
		if (isNaN(date)) {
			return "Not logged in";
		}
		return formatDate(dateString);
	};

	const handleOpenEditModal = useCallback((clickedUser) => {
		setSelectedUser(clickedUser);
		setShowEditModal(true);
	}, []);

	const handleCloseEditModal = useCallback(() => {
		setShowEditModal(false);
		setSelectedUser(null);
	}, []);

	const handleCloseAddUserModal = useCallback(() => {
		setShowAddUserModal(false);
	}, []);

	const handleOpenAddUserModal = useCallback(() => {
		setSelectedUser({
			Id: null,
			FirstName: "",
			LastName: "",
			Username: "",
			RoleId: CompanyUserRole.Learner
		});
		setShowAddUserModal(true);
	}, []);

	const editButtonTemplate = (rowData) => {
		return (
			<Button type={ButtonType.Primary} icon={faPenToSquare}  label={"Edit"} onClick={() => handleOpenEditModal(rowData)}  />
		);
	};

	function getAccessLevelLabel(rowData) {
		const idNum = Number(rowData?.RoleId);
		const label = roleLabel(idNum);
		if (label && label !== String(idNum)) {
			return label;
		}
		return rowData?.RoleName ?? "";
	}

	const columns = [
		{
			field: "FirstName",
			headerName: "First Name",
		},
		{
			field: "LastName",
			headerName: "Last Name",
		},
		{
			field: "Username",
			headerName: "Username",
		},
		{
			field: "RoleId",
			headerName: "Access Level",
			valueGetter: (rowData) => getAccessLevelLabel(rowData),
			style: { minWidth: "160px" }
		},
		{
			field: "LastLoginDate",
			headerName: "Last Login",
			valueGetter: (rowData) => handleDate(rowData.LastLoginDate),
		},
		{
			field: "LastReminderDate",
			headerName: "Registration Invite sent",
			valueGetter: (rowData) => rowData.LastReminderDate
		},
		{
			headerName: '',
			valueGetter: editButtonTemplate,
			style: { minWidth: '120px' },
			sortable: false,
			removeHeaderFromMobile: true
		}
	];


	const ViewContent = (
		<>
			{(loadingCustomer || loadingPackages || isSubmitting)
				? <LoadingSpinner active={(loadingCustomer || loadingPackages || isSubmitting)} />
				: data &&
					<div>
						<h4>Name</h4>
						<p className="mt-0">{data.Name}</p>
						<SelectPackages {...data} selectedPackages={props.item?.Packages} packageData={availablePackages} onChangeInput={onChangeInput} isValid={setCanSubmit} disableUserModification={disableUserModification} title={"Package Details"} />
						<h4>Users</h4>
						<div className="add-user">
							<Button type={ButtonType.Primary} className="add-user-btn" label="Add New User +" onClick={handleOpenAddUserModal} />
						</div>
						<DataTable columns={columns} rows={users} columnIdName="Id" className="p-datatable-sm" />
						<div className="add-user">
							<Button type={ButtonType.Primary} className="add-user-btn" label="Add New User +" onClick={handleOpenAddUserModal} />
						</div>
					</div>
			}
		</>
	);

	const buttons = <>
		{!disableUserModification && (
			data ? (
				<Button type={ButtonType.Tertiary} label={`Delete ${data.Name}`} onClick={onClickDelete} />
			) : (
				<Button type={ButtonType.Tertiary} label={"Delete"} onClick={onClickDelete} />
			)
		)}
		{!customerAdminHasLoggedIn && <Button type={ButtonType.Secondary} icon={faEnvelope} label={"Resend Registration Email"} onClick={onClickResend} />}
		{!disableUserModification && < Button
			type={ButtonType.Primary}
			disabled={!canSubmit || isSubmitting}
			icon={faCircleCheck}
			label={"Confirm Packages"}
			onClick={(e, c) => onClickSubmit(c)}
			disableUntilClickCompleted={true}
		/>}
	</>;

	const ResendModal = (
		data &&
		<MessageModal title="Confirm Resend" type={MessageModalType.Question} onClick={onClickConfirmResend} size="m">
			Are you sure you want to resend the registration email to {data.Name}?
		</MessageModal>
	);

	const ErrorModal = (
		<MessageModal title="Error" onClick={onCloseModal} size="m">
			<p>{errorMessage}</p>
		</MessageModal>
	);

	const FatalErrorModal = (
		<MessageModal title="An Error Occured" type={MessageModalType.Information} size="m" onClick={handleCloseModal}>
			An error occurred when trying to resend the registration email. Please try again. If the problem persists, please contact ITEL.
		</MessageModal>
	);


	const DeleteModal = (
		data &&
		<MessageModal title="Confirm Customer Deletion" type={MessageModalType.Question} onClick={onClickConfirmDelete} size="m">
			Are you sure you want to delete {data.Name}?
		</MessageModal>
	);

	const ViewModal = (
		<Modal
			title="Customer Details"
			showModal={props.showModal}
			content={ViewContent}
			size="l"
			modalButtons={buttons}
			close={onClickClose}
		/>
	);

	const UserDeleteModal = (
		userPendingDelete && (
			<MessageModal
				title="Confirm User Deletion"
				type={MessageModalType.Question}
				onClick={onClickConfirmDeleteUser}
				size="m"
			>
				Are you sure you want to delete {getUserFullName(userPendingDelete)}?
			</MessageModal>
		)
	);


	const render = () => {
		if (reason !== LogoutReason.None) {
			return <AuthLogoutMessage reason={reason} />;
		}
		if (executingFetch) {
			return <LoadingSpinner active={executingFetch} />;
		}
		if (confirmResend) {
			return ResendModal;
		}
		if (fatalError) {
			return FatalErrorModal;
		}
		if (showErrorModal) {
			return ErrorModal;
		}
		if (confirmDelete) {
			return (DeleteModal);
		}
		if (confirmUserDelete) {
			return UserDeleteModal;
		}
		return (
			<>
				{ViewModal}

				{confirmSaveOpen && (
					<MessageModal
						title="Confirm Save"
						type={MessageModalType.Question}
						onClick={onClickConfirmSave}
						size="m"
					>
						Are you sure you want to save these changes?
					</MessageModal>
				)}

				{showEditModal && selectedUser && (
					<EditCustomerUser
						title={'Edit User'}
						open={showEditModal}
						showModal={showEditModal}
						selectedUser={selectedUser}
						setOpenEditor={setShowEditModal}
						onCancel={handleCloseEditModal}
						onSaved={(updatedUser) => handleUserSaved(updatedUser, data?.CompanyId)}
						onRequestDelete={handleRequestUserDelete}
						allowDelete={true}
						roleLabelFn={roleLabel}
						roleOptions={AssignableRoleOptionsBase}
						emailReadOnly={true}
					/>
				)}

				{showAddUserModal && (
					<EditCustomerUser
						title={'Add User'}
						open={showAddUserModal}
						showModal={showAddUserModal}
						selectedUser={selectedUser}
						setOpenEditor={setShowEditModal}
						onCancel={handleCloseAddUserModal}
						onSaved={(newUser) => handleUserAdded(newUser, data?.CompanyId)}
						onRequestSave={handleRequestSave}
						allowDelete={false}
						roleLabelFn={roleLabel}
						roleOptions={AssignableRoleOptionsBase}
						emailReadOnly={false}
					/>
				)}
			</>
		);
	}

	return (
		<>{render()}</>
	);
};

export default ViewCustomer;